import { atom, atomFamily } from 'recoil';
import { selector, selectorFamily } from './recoilSelectors';
import { MediumCard, VisibleCards } from '../components/skeleton/skeletonTypes';
import { ALL_CARDS, MEDIUM_CARD_ONLY, NO_CARDS, SMALL_CARD_ONLY } from '../constants/cards';
import { DISPATCH_PLAN_PAGES, PAGE } from '../constants/pages';
import { pageState } from "./appState";
import { getWindowSize } from "../utils/window";
import { actionPanelModeState } from "./actionPanelState";
import { drawModeState } from "./drawModeState";
import { localStorageEffect } from '../utils/persistence';
import { boardMemberUidsState, boardVisibleSequencesState } from "./boardState";
import { dispatchTreeActiveCardState, findAssigneeForJobState } from "./viewState";
import { recurringStopEditState } from "./recurringState";
import { companyFieldState } from "./accountState";
import { isLocationOnlyPlan } from "../utils/account";
import { resolveVisibleCardSizes } from "../utils/board";

const MAP_PADDING_KEY = 'MapPadding';
const ACTIONS_KEY = 'Actions';
const LAYERS_CARD_KEY = 'LayersCard';
const MEDIUM_CARD_KEY = 'MediumCard';

export const mediumState = atom({
    key: MEDIUM_CARD_KEY,
    default: {} as MediumCard,
});

export const actionsIsOpenState = selector({
    key: ACTIONS_KEY,
    get: ({ get }) => get(actionPanelModeState) !== "none",
});

export const layersCardIsOpenState = atom({
    key: LAYERS_CARD_KEY,
    default: false,
});

/** The map dock's size while it is on the map, so controls at the bottom of the map can keep clear of it. */
export const mapDockBoxState = atom<{ width: number; height: number } | undefined>({
    key: "map.dock.box",
    default: undefined,
});

/** The "people hidden by filters" pill while it shows, so the setup pill can sit on top of it. */
export const peopleHiddenPillBoxState = atom<{ height: number; bottom: number } | undefined>({
    key: "map.peopleHiddenPill.box",
    default: undefined,
});

export const mapPaddingState = atom({
    key: MAP_PADDING_KEY,
    default: 0,
});

export const dispatchBoardIsEmptyState = selector({
    key: "app.cards.dispatch.smallOnly",
    get: ({ get }) => {
        const membersCount = get(boardMemberUidsState).length;
        const sequencesCount = get(boardVisibleSequencesState).length;
        const dispatchCard = get(dispatchTreeActiveCardState);

        return membersCount + sequencesCount === 0 && !dispatchCard;
    }
});

export const isSmallCardVisibleState = selector({
    key: "app.card.small.visible",
    get: ({ get }) => get(visibleCardsState).smallIsVisible
});

export const isMediumCardVisibleState = selector({
    key: "app.card.small.medium",
    get: ({ get }) => get(visibleCardsState).mediumIsVisible
});

/**
 * The New job card on Dispatch leaves the people list and the route in view, read-only, so the person the job is
 * for stays in sight; anywhere else, and on a window too narrow for the cards beside the map, the cards step aside.
 */
export const cardsBehindComposerState = selector({
    key: "app.cards.behindComposer",
    get: ({ get }) => get(drawModeState)?.composer === "job" && get(pageState) === PAGE.DISPATCH
        && get(windowSizeState).innerWidth >= 768 && !get(windowIsMobileState),
});

const visibleCardsState = selector<VisibleCards>({
    key: 'app.cards.visible',
    get: ({ get }) => {
        const page = get(pageState);
        const drawMode = get(drawModeState);
        const stripePlan = get(companyFieldState("stripe_plan"));

        // a new recurring route's stop picked on the map: the route's card stays, or the unsaved route and the stop close
        const pickingRecurringStop = page === PAGE.DISPATCH && get(dispatchTreeActiveCardState) === "recurring"
            && drawMode?.type === "job" && !!get(recurringStopEditState);
        if (drawMode && drawMode.type !== "team" && !get(cardsBehindComposerState) && !pickingRecurringStop || page === PAGE.MAP) {
            return NO_CARDS;
        }
        if (DISPATCH_PLAN_PAGES.includes(page) && isLocationOnlyPlan(stripePlan)) {
            return MEDIUM_CARD_ONLY;
        }
        if (page === PAGE.TEAMS) {
            return MEDIUM_CARD_ONLY;
        }
        if (page === PAGE.MEMBERS) {
            return MEDIUM_CARD_ONLY;
        }
        if (page === PAGE.LIVE) {
            return SMALL_CARD_ONLY;
        }
        if (page === PAGE.DISPATCH) {
            // Recurring routes: the people list folds, the card takes its place and the map shows the route
            if (get(dispatchTreeActiveCardState) === "recurring") {
                return MEDIUM_CARD_ONLY;
            }
            const boardIsEmpty = get(dispatchBoardIsEmptyState);
            if (boardIsEmpty) {
                return SMALL_CARD_ONLY;
            }
            // On mobile, only show medium card when board has content (member selected)
            const isMobile = get(windowIsMobileState);
            if (isMobile) {
                return MEDIUM_CARD_ONLY;
            }
            return ALL_CARDS;
        }
        if (page === PAGE.JOBS) {
            return MEDIUM_CARD_ONLY
        }
        if (page === PAGE.PLACES) {
            return MEDIUM_CARD_ONLY;
        }
        if (page === PAGE.ZONES) {
            return MEDIUM_CARD_ONLY;
        }
        if (page === PAGE.FORMS) {
            return ALL_CARDS;
        }
        if (page === PAGE.REPORTS) {
            return MEDIUM_CARD_ONLY
        }
        if (page === PAGE.ALERTS) {
            return ALL_CARDS;
        }
        if (page === PAGE.MESSAGES) {
            return MEDIUM_CARD_ONLY;
        }
        if (page === PAGE.TIMESHEET) {
            return MEDIUM_CARD_ONLY;
        }
        if (page === PAGE.STATS) {
            return MEDIUM_CARD_ONLY;
        }
        if (page === PAGE.INTEGRATIONS) {
            return MEDIUM_CARD_ONLY;
        }
        if (page === PAGE.SETTINGS) {
            return MEDIUM_CARD_ONLY;
        }
        if (page === PAGE.HELP) {
            return MEDIUM_CARD_ONLY;
        }
        if (page === PAGE.HEATMAP) {
            return SMALL_CARD_ONLY;
        }
        if (page === PAGE.PHOTOS) {
            return MEDIUM_CARD_ONLY;
        }
        if (page === PAGE.LOCATION) {
            return MEDIUM_CARD_ONLY;
        }
        if (page === PAGE.REPLAYS) {
            return MEDIUM_CARD_ONLY;
        }
        if (page === PAGE.PLANNER) {
            return MEDIUM_CARD_ONLY;
        }
        if (page === PAGE.ACTIVITYLOG) {
            return MEDIUM_CARD_ONLY;
        }
        if (page === PAGE.RESELLER) {
            return MEDIUM_CARD_ONLY;
        }
        if (page === PAGE.AFFILIATE) {
            return MEDIUM_CARD_ONLY;
        }
        return NO_CARDS;
    }
});

export const mediumWidthState = atom({
    key: 'app.cards.medium.width',
    default: 0,
});

export const windowSizeState = atom({
    key: "window.size",
    default: getWindowSize()
});

export const windowIsMobileState = selector({
    key: "window.mobile",
    get: ({ get }) => {
        const size = get(windowSizeState);
        return size.innerWidth < 768 && /Mobi|iPhone|Android(?!.*Tablet)/i.test(navigator.userAgent);
    }
});

export const customMediumCardWidthState = atomFamily<number, string>({
    key: "app.cards.medium.width.custom",
    default: 0,
    effects: [
        localStorageEffect,
    ]
});

export type CardSizes = {
    smallCardSize: number;
    mediumCardSize: number;
    mapCardSize: number;
}

export const smallCardSizeState = atomFamily<number, string>({
    key: "app.card.small.size",
    default: selectorFamily<number, string>({
        key: "app.cards.sizes.initializer",
        get: (page) => ({ get }) => {
            const defaults = get(panelsDefaultState(page))
            return defaults.defaultSmallCardSize;
        }
    }),
});

export const mediumCardSizeState = atomFamily<number, string>({
    key: "app.card.medium.size",
    default: selectorFamily<number, string>({
        key: "app.cards.sizes.initializer",
        get: (page) => ({ get }) => {
            const defaults = get(panelsDefaultState(page))
            return defaults.defaultMediumCardSize;
        }
    }),
});

export const mapCardSizeState = atomFamily<number, string>({
    key: "app.card.map.size",
    default: selectorFamily<number, string>({
        key: "app.cards.sizes.initializer",
        get: (page) => ({ get }) => {
            const defaults = get(panelsDefaultState(page))
            return defaults.defaultMapSize;
        }
    }),
});

/**
 * Pages whose content has no map linkage: they own the whole width (the medium card takes
 * whatever the small card leaves) instead of floating over an idle map.
 */
const FULL_WIDTH_PAGES: string[] = [
    PAGE.STATS, PAGE.PHOTOS, PAGE.HELP, PAGE.RESELLER, PAGE.AFFILIATE, PAGE.REPLAYS, PAGE.PLANNER, PAGE.CODISPATCH,
    PAGE.REPORTS, PAGE.TIMESHEET, PAGE.ACTIVITYLOG, PAGE.FORMS, PAGE.ALERTS, PAGE.MESSAGES,
];

/** Pages whose cards own the whole width; the list card beside them stays resizable. */
export function isFullWidthPage(page: string): boolean {
    return FULL_WIDTH_PAGES.includes(page);
}

export const panelsDefaultState = selectorFamily<{ defaultSmallCardSize: number; defaultMediumCardSize: number; defaultMapSize: number }, string>({
    key: "app.card.sizes.default",
    get:
        (page) =>
        ({ get }) => {
            let defaultSmallCardSize = 30;
            let defaultMediumCardSize = 40;

            if (page === PAGE.PLACES || page === PAGE.LOCATION || page === PAGE.MESSAGES) {
                defaultMediumCardSize = 50;
            }
            if (page === PAGE.INTEGRATIONS) {
                defaultMediumCardSize = 60;
            }
            if (page === PAGE.REPORTS || page === PAGE.TIMESHEET) {
                defaultMediumCardSize = 80;
            }
            if (page === PAGE.MEMBERS || page === PAGE.TEAMS || page === PAGE.ACTIVITYLOG) {
                defaultMediumCardSize = 80;
            }
            if (page === PAGE.ALERTS || page === PAGE.FORMS) {
                defaultSmallCardSize = 25;
                defaultMediumCardSize = 60;
            }
            if (page === PAGE.DISPATCH) {
                defaultSmallCardSize = 26;
                defaultMediumCardSize = get(dispatchTreeActiveCardState) === "recurring" ? 38 : 40;
            }
            if (page === PAGE.JOBS) {
                defaultMediumCardSize = 70;
            }
            const { smallIsVisible, mediumIsVisible } = get(visibleCardsState);
            defaultSmallCardSize = smallIsVisible ? defaultSmallCardSize : 0;
            defaultMediumCardSize = mediumIsVisible ? defaultMediumCardSize : 0;
            if (mediumIsVisible && FULL_WIDTH_PAGES.includes(page)) {
                defaultMediumCardSize = 100 - defaultSmallCardSize;
            }

            const defaultMapSize = 100 - defaultSmallCardSize - defaultMediumCardSize;

            return {
                defaultSmallCardSize,
                defaultMediumCardSize,
                defaultMapSize,
            };
        },
});

/** True when the cards leave no room for the map, e.g. Statistics or Timesheet full screen. */
export const isMapHiddenState = selectorFamily<boolean, string>({
    key: "app.map.hidden",
    get: (page) => ({ get }) => {
        const { smallIsVisible, mediumIsVisible } = get(visibleCardsState);
        const { defaultSmallCardSize, defaultMediumCardSize } = get(panelsDefaultState(page));
        const sizes = resolveVisibleCardSizes({
            smallIsVisible,
            mediumIsVisible,
            smallCardSize: get(smallCardSizeState(page)),
            mediumCardSize: get(mediumCardSizeState(page)),
            mapCardSize: get(mapCardSizeState(page)),
            defaultSmallCardSize,
            defaultMediumCardSize,
        });
        return sizes.mapCardSize === 0;
    }
});

/** The page chrome (sidebar, top bar, cards) goes solid when no map is visible behind it. */
export const isSolidChromeState = selector<boolean>({
    key: "app.chrome.solid",
    get: ({ get }) => get(isMapHiddenState(get(pageState))),
});
